import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import {
    ComposedChart, Bar, Cell, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from "recharts";
import { tariff_comparison_analysis } from "../api/bill";

// ─── Colour tokens ────────────────────────────────────────────────────────────
const C_PURPLE = "#7e4a9d";
const C_BLUE = "#1565c0";
const C_GREEN = "#2e7d32";
const C_RED = "#c62828";
const C_BORDER = "#ede7f6";
const C_GRID = "#f0e6ff";

// ─── Tariff label mappings ────────────────────────────────────────────────────
const tariffLabelMap = {
    LVG: "LVG (General Tariff)",
    LVT: "LVT (ToU Tariff)",
    MVG: "MVG (General Tariff)",
    MVT: "MVT (ToU Tariff)",
    HVG: "HVG (General Tariff)",
    HVT: "HVT (ToU Tariff)",
};

const compareTariffMap = {
    LVG: "LVT", LVT: "LVG",
    MVG: "MVT", MVT: "MVG",
    HVG: "HVT", HVT: "HVG",
};

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, userTariff }) => {
    if (!active || !payload?.length) return null;

    const compareTariff = compareTariffMap[userTariff] ?? "Compare";

    return (
        <Box sx={{
            background: "#fff",
            border: `1px solid ${C_BORDER}`,
            borderRadius: 2,
            p: "10px 14px",
            boxShadow: "0 4px 16px rgba(94,53,177,0.1)",
            minWidth: 280,
        }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5, color: "#333" }}>
                {label}
            </Typography>
            {payload.map((entry) => {
                // determine label
                let name = entry.name;
                if (entry.dataKey === "actualBill") name = tariffLabelMap[userTariff] ?? userTariff;
                if (entry.dataKey === "compareBill") name = tariffLabelMap[compareTariff] ?? compareTariff;

                // saving bar — dynamic color
                const isSaving = entry.dataKey === "saving";
                const savingColor = isSaving
                    ? (entry.value < 0 ? C_GREEN : C_RED)
                    : entry.color;

                return (
                    <Box key={entry.dataKey} sx={{ display: "flex", alignItems: "center", gap: 1, mb: "3px" }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "2px", background: savingColor, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 13, color: "#555", flex: 1 }}>{name}:</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: savingColor }}>
                            RM {Number(entry.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            {isSaving && (
                                <span style={{ fontSize: 10, marginLeft: 4 }}>
                                    {entry.value < 0 ? `(${userTariff} cheaper ✓)` : `(${userTariff} more expensive)`}
                                </span>
                            )}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
};

// ─── Custom Legend ────────────────────────────────────────────────────────────
const CustomLegend = ({ userTariff }) => {
    const compareTariff = compareTariffMap[userTariff] ?? "Compare";
    return (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, flexWrap: "wrap", mb: 1 }}>
            {[
                { color: C_PURPLE, label: tariffLabelMap[userTariff] ?? userTariff },
                { color: C_BLUE, label: tariffLabelMap[compareTariff] ?? compareTariff },
                // { color: C_GREEN, label: `Saving (${userTariff} cheaper)` },
                // { color: C_RED, label: `Saving (${compareTariffMap[userTariff]} cheaper)` },
            ].map(({ color, label }) => (
                <Box key={label} sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "2px", background: color }} />
                    <Typography sx={{ fontSize: 14, color: "#555" }}>{label}</Typography>
                </Box>
            ))}
        </Box>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const TariffComparisonAnalysis = ({ siteId, userTariff }) => {
    const [chartData, setChartData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAnalysis = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const res = await tariff_comparison_analysis(siteId, userTariff);
                const data = res?.data?.data ?? [];
                setChartData(
                    data.map((d) => {
                        const mismatch = d.actual_tariff !== userTariff;
                        const actual = mismatch ? d.tariff_compare_total_bill : d.actual_total_bill;
                        const compare = mismatch ? d.actual_total_bill : d.tariff_compare_total_bill;
                        return {
                            month: d.month,
                            actualBill: +parseFloat(actual).toFixed(2),
                            compareBill: +parseFloat(compare).toFixed(2),
                            saving: +parseFloat(actual - compare).toFixed(2),
                            displaySaving: +parseFloat(compare - actual).toFixed(2),
                        };
                    })
                );
            } catch (err) {
                console.error("tariff_comparison_analysis error:", err);
                setError("Failed to load analysis data.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchAnalysis();
    }, [siteId, userTariff]);

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
                <CircularProgress sx={{ color: C_PURPLE }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const totalNet = chartData.reduce((s, d) => s + d.saving, 0);
    const compareTariff = compareTariffMap[userTariff] ?? "Compare";

    return (
        <Box sx={{ p: 1 }}>
            {/* ── Summary chips ── */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                {[
                    {
                        label: "Current Tariff",
                        value: userTariff,
                        color: C_PURPLE,
                        subLabel: tariffLabelMap[userTariff] ?? "",
                    },
                    {
                        label: tariffLabelMap[userTariff] ?? userTariff,
                        value: `RM ${chartData.reduce((s, d) => s + d.actualBill, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        color: C_PURPLE,
                    },
                    {
                        label: tariffLabelMap[compareTariff] ?? compareTariff,
                        value: `RM ${chartData.reduce((s, d) => s + d.compareBill, 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        color: C_BLUE,
                    },
                    {
                        label: "Net saving (current vs compare)",
                        value: `RM ${totalNet < 0 ? "" : "-"}${Math.abs(totalNet).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                        color: totalNet < 0 ? C_GREEN : C_RED,
                        subLabel: totalNet < 0
                            ? `${userTariff} is cheaper ✓`
                            : `${compareTariff} is cheaper ✓`,
                    },
                ].map(({ label, value, color, subLabel }) => (
                    <Box
                        key={label}
                        sx={{
                            background: "#fff",
                            border: `1px solid ${C_BORDER}`,
                            borderRadius: 2,
                            px: 3, py: 1.5, minWidth: 160,
                            boxShadow: "0 2px 8px rgba(94,53,177,0.07)",
                        }}
                    >
                        <Typography sx={{ fontSize: 13, color: "#888", mb: "4px" }}>{label}</Typography>
                        <Typography sx={{ fontSize: 22, fontWeight: 700, color }}>{value}</Typography>
                        {subLabel && (
                            <Typography sx={{ fontSize: 12, color, mt: "4px" }}>{subLabel}</Typography>
                        )}
                    </Box>
                ))}
            </Box>



            {/* ── Saving Chart ── */}
            <Box sx={{ mt: 4 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#555", mb: 1 }}>
                    Monthly Saving / Loss ({tariffLabelMap[userTariff]} vs {tariffLabelMap[compareTariff]})
                </Typography>
                <Box sx={{ width: "90%", height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                            data={chartData}
                            margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" stroke={C_GRID} />
                            <XAxis dataKey="month" tick={{ fontSize: 14, fill: "#555" }} />
                            <YAxis
                                yAxisId="saving"
                                tick={{ fontSize: 14, fill: "#555" }}
                                tickFormatter={(v) => `RM ${v.toLocaleString()}`}
                                width={110}
                                domain={([dataMin, dataMax]) => {
                                    const range = Math.abs(dataMax - dataMin);
                                    const buffer = range > 0
                                        ? range * 0.15
                                        : Math.abs(dataMin || dataMax) * 0.2 || 100; // fallback if single point

                                    return [
                                        Math.min(dataMin - buffer, 0),   // always include 0
                                        Math.max(dataMax + buffer, 0)    // always include 0
                                    ];
                                }}
                            />
                            {/* Zero reference line */}
                            <ReferenceLine yAxisId="saving" y={0} stroke="#999" strokeWidth={1.5} />
                            <Tooltip
                                content={({ active, payload, label }) => {
                                    if (!active || !payload?.length) return null;
                                    const val = payload[0]?.value ?? 0;  // this is now displaySaving
                                    return (
                                        <Box sx={{
                                            background: "#fff",
                                            border: `1px solid ${C_BORDER}`,
                                            borderRadius: 2,
                                            p: "10px 14px",
                                            boxShadow: "0 4px 16px rgba(94,53,177,0.1)",
                                            minWidth: 220,
                                        }}>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5, color: "#333" }}>
                                                {label}
                                            </Typography>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Box sx={{ width: 10, height: 10, borderRadius: "2px", background: val > 0 ? C_GREEN : C_RED }} />  {/* ✅ was val < 0 */}
                                                <Typography sx={{ fontSize: 13, color: "#555", flex: 1 }}>Saving:</Typography>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: val > 0 ? C_GREEN : C_RED }}>
                                                    RM {val < 0 ? "-" : ""}{Math.abs(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    <span style={{ fontSize: 10, marginLeft: 4 }}>
                                                        {val > 0 ? `(${userTariff} cheaper ✓)` : `(${compareTariff} cheaper ✓)`}
                                                    </span>
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                }}
                            />
                            <Bar yAxisId="saving" dataKey="displaySaving" radius={[4, 4, 0, 0]} barSize={28}>
                                {chartData.map((entry, index) => (
                                    <Cell
                                        key={`saving-cell-${index}`}
                                        fill={entry.displaySaving > 0 ? C_GREEN : C_RED}  // ✅ flipped condition too
                                        fillOpacity={0.85}
                                    />
                                ))}
                            </Bar>
                        </ComposedChart>
                    </ResponsiveContainer>
                </Box>
            </Box>

            <Typography sx={{ fontSize: 13, color: "#aaa", mt: 1.5, textAlign: "center" }}>
                Saving bar:&nbsp;
                <span style={{ color: C_GREEN }}>green = {userTariff} cheaper</span>
                &nbsp;|&nbsp;
                <span style={{ color: C_RED }}>red = {compareTariffMap[userTariff]} cheaper</span>
            </Typography>

            {/* ── Chart ── */}
            <Box sx={{ width: "90%", height: 420 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C_GRID} />
                        <XAxis dataKey="month" tick={{ fontSize: 14, fill: "#555" }} />

                        {/* Single Y-axis — shared for all bars */}
                        <YAxis
                            yAxisId="bill"
                            tick={{ fontSize: 14, fill: "#555" }}
                            tickFormatter={(v) => `RM ${v.toLocaleString()}`}
                            width={110}
                        />

                        <Tooltip content={<CustomTooltip userTariff={userTariff} />} />
                        <Legend content={<CustomLegend userTariff={userTariff} />} />

                        {/* Current tariff bill bar */}
                        <Bar
                            yAxisId="bill"
                            name="Current tariff bill"
                            dataKey="actualBill"
                            fill={C_PURPLE}
                            fillOpacity={0.85}
                            radius={[4, 4, 0, 0]}
                            barSize={28}
                        />

                        {/* Compare tariff bill bar */}
                        <Bar
                            yAxisId="bill"
                            name="Compare tariff bill"
                            dataKey="compareBill"
                            fill={C_BLUE}
                            fillOpacity={0.75}
                            radius={[4, 4, 0, 0]}
                            barSize={28}
                        />

                        {/* Saving bar — green if negative (cheaper), red if positive (more expensive) */}
                        {/* <Bar
                            yAxisId="bill"
                            name="Saving"
                            dataKey="saving"
                            radius={[4, 4, 0, 0]}
                            barSize={28}
                        >
                            {chartData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={entry.saving < 0 ? C_GREEN : C_RED}
                                    fillOpacity={0.85}
                                />
                            ))}
                        </Bar> */}

                    </ComposedChart>
                </ResponsiveContainer>
            </Box>

        </Box>

    );
};

export default TariffComparisonAnalysis;